import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreateGroupBody,
  GalleryGroupAccessLevel,
  GroupDto,
  GroupFolderDto,
  GroupGalleryDto,
  UpdateGroupBody,
  UserDto,
} from '@nusign/picflow-backend-types';
import { combineLatest, delay, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { billingScopes } from '../billing-consts';
import { standardMemoryCacheExpiry } from '../consts';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { AppConfigService } from '../providers/config.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';

export type GroupFolderMin = GroupFolderDto & { group: string; folder: string; access: GalleryGroupAccessLevel };
export type GroupGalleryMin = GroupGalleryDto & { group: string; gallery: string; access: GalleryGroupAccessLevel };

@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private permissionService = inject(PermissionService);

  public getUserGroups(): Observable<GroupDto[]> {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of([]);
    return this.loader.get(
      'groups',
      () => this.http.get<{ data: GroupDto[] }>(`${this.config.apiEndpoint}/groups`).pipe(map(resp => resp.data)),
      standardMemoryCacheExpiry
    );
  }

  public createUserGroup(group: CreateGroupBody): Observable<GroupDto> {
    return this.http.post<GroupDto>(this.config.apiEndpoint + '/groups', group).pipe(
      delay(2000), // spicedb delay
      tap(group => {
        this.loader.update<GroupDto[]>('groups', groups => {
          if (!groups) return;
          groups.push(group);
          return groups;
        });
      })
    );
  }

  public updateUserGroup(groupId: string, params: UpdateGroupBody): Observable<GroupDto> {
    return this.http.patch<GroupDto>(this.config.apiEndpoint + '/groups/' + groupId, params).pipe(
      tap(group => {
        this.loader.update<GroupDto[]>('groups', groups => {
          if (!groups) return;
          const index = groups.findIndex(g => g.id === groupId);
          if (index === -1) return;
          groups[index] = group;
          return groups;
        });
      })
    );
  }

  public deleteUserGroup(groupId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}`).pipe(
      tap(() => {
        this.loader.update<GroupDto[]>('groups', groups => {
          if (!groups) return;
          const index = groups.findIndex(g => g.id === groupId);
          if (index === -1) return;
          groups.splice(index, 1);
          return groups;
        });
        this.loader.updateFuzzy<GroupGalleryMin[]>('group_galleries/', galleryGroups => {
          if (!galleryGroups) return;
          return galleryGroups.filter(gg => gg.group !== groupId);
        });
        this.loader.updateFuzzy<GroupFolderMin[]>(`group_folders/`, folderGroups => {
          if (!folderGroups) return;
          return folderGroups.filter(gg => gg.group !== groupId);
        });
      })
    );
  }

  public getUserGroupUsers(groupId: string): Observable<UserDto[]> {
    return this.loader.get(`groups/${groupId}/users`, () => {
      return this.http
        .get<{ data: UserDto[] }>(`${this.config.apiEndpoint}/groups/${groupId}/users`)
        .pipe(map(resp => resp.data));
    });
  }

  public addUserToGroup(groupId: string, userId: string): Observable<void> {
    return this.http.put<void>(`${this.config.apiEndpoint}/groups/${groupId}/users/${userId}`, {}).pipe(
      tap(() => {
        this.loader.update<UserDto[]>(`groups/${groupId}/users`, users => {
          if (!users) return;
          users.push(<UserDto>{ id: userId }); // should be the full user object
          return users;
        });
      })
    );
  }

  public removeUserFromGroup(groupId: string, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/users/${userId}`).pipe(
      tap(() => {
        this.loader.update<UserDto[]>(`groups/${groupId}/users`, users => {
          if (!users) return;
          const index = users.findIndex(u => u.id === userId);
          if (index === -1) return;
          users.splice(index, 1);
          return users;
        });
      })
    );
  }

  public removeAll(galleryId: string) {
    return this.getGalleryUserGroups(galleryId).pipe(
      take(1),
      switchMap(groups => forkJoinSafe(groups.map(g => this.removeUserGroupFromGallery(g.group, g.gallery))))
    );
  }

  public getGalleryUserGroups(galleryId: string) {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of([]);
    return this.getAllGroupGalleries().pipe(map(groups => groups.filter(g => g.gallery === galleryId)));

    return this.loader.get(`group_galleries/${galleryId}`, () => {
      return this.getAllService.getAll<GroupGalleryMin>(
        `${this.config.apiEndpoint}/galleries/${galleryId}/group_galleries`
      );
    });
  }

  public getFolderUserGroups(folderId: string) {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of([]);
    return this.getAllGroupFolders().pipe(
      map(groups => {
        return groups.filter(g => g.folder === folderId);
      })
    );

    return this.loader.get(`group_folders/${folderId}`, () => {
      return this.getAllService.getAll<GroupFolderMin>(`${this.config.apiEndpoint}/folders/${folderId}/group_folders`);
    });
  }

  public getGroupGalleries(groupId: string) {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of([] as GroupGalleryMin[]);
    return this.loader.get(`group_galleries/${groupId}`, () => {
      return this.getAllService.getAll<GroupGalleryMin>(`${this.config.apiEndpoint}/groups/${groupId}/group_galleries`);
    });
  }

  public getGroupFolders(groupId: string) {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of([]);
    return this.loader.get(`group_folders/${groupId}`, () => {
      return this.getAllService.getAll<GroupFolderMin>(`${this.config.apiEndpoint}/groups/${groupId}/group_folders`);
    });
  }

  public addUserGroupToGallery(groupId: string, galleryId: string, access: GalleryGroupAccessLevel): Observable<void> {
    return this.http.put<void>(`${this.config.apiEndpoint}/groups/${groupId}/galleries/${galleryId}`, { access }).pipe(
      tap(() => {
        this.loader.update<GroupGalleryMin[]>(`group_galleries/${galleryId}`, galleryGroups => {
          if (!galleryGroups) return;
          galleryGroups.push(<GroupGalleryMin>{ group: groupId, access, gallery: galleryId });
          return galleryGroups;
        });
        this.loader.update<GroupGalleryMin[]>(`group_galleries/${groupId}`, galleryGroups => {
          if (!galleryGroups) return;
          galleryGroups.push(<GroupGalleryMin>{ group: groupId, access, gallery: galleryId });
          return galleryGroups;
        });
      })
    );
  }

  public addUserGroupToFolder(groupId: string, folderId: string, access: GalleryGroupAccessLevel): Observable<void> {
    return this.http.put<void>(`${this.config.apiEndpoint}/groups/${groupId}/folders/${folderId}`, { access }).pipe(
      tap(() => {
        this.loader.update<GroupFolderMin[]>(`group_folders/${groupId}`, folderGroups => {
          if (!folderGroups) return;
          folderGroups.push(<GroupFolderMin>{ group: groupId, access, folder: folderId });
          return folderGroups;
        });
        this.loader.update<GroupFolderMin[]>(`group_folders/${folderId}`, folderGroups => {
          if (!folderGroups) return;
          folderGroups.push(<GroupFolderMin>{ group: groupId, access, folder: folderId });
          return folderGroups;
        });
      }),
      map(() => undefined)
    );
  }

  public updateGalleryUserGroupAccessLevel(
    groupId: string,
    galleryId: string,
    access: GalleryGroupAccessLevel
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.config.apiEndpoint}/groups/${groupId}/galleries/${galleryId}`, { access })
      .pipe(
        tap(() => {
          this.loader.updateFuzzy<GroupGalleryMin[]>(`group_galleries`, galleryGroups => {
            if (!galleryGroups) return;
            const index = galleryGroups.findIndex(gg => gg.group === groupId);
            if (index === -1) return;
            galleryGroups[index].access = access;
            return galleryGroups;
          });
        })
      );
  }

  public updateFolderUserGroupAccessLevel(
    groupId: string,
    folderId: string,
    access: GalleryGroupAccessLevel
  ): Observable<void> {
    return this.http.patch<void>(`${this.config.apiEndpoint}/groups/${groupId}/folders/${folderId}`, { access }).pipe(
      tap(() => {
        this.loader.updateFuzzy<GroupFolderMin[]>(`group_folders`, galleryGroups => {
          if (!galleryGroups) return;
          const index = galleryGroups.findIndex(gg => gg.group === groupId);
          if (index === -1) return;
          galleryGroups[index].access = access;
          return galleryGroups;
        });
      })
    );
  }

  public removeUserGroupFromGallery(groupId: string, galleryId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/galleries/${galleryId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<GroupGalleryMin[]>(`group_galleries/${galleryId}`, galleryGroups => {
          if (!galleryGroups) return;
          return galleryGroups.filter(gg => gg.gallery !== galleryId || gg.group !== groupId);
        });
        this.loader.updateFuzzy<GroupGalleryMin[]>(`group_galleries/${groupId}`, galleryGroups => {
          if (!galleryGroups) return;
          return galleryGroups.filter(gg => gg.gallery !== galleryId || gg.group !== groupId);
        });
      })
    );
  }

  public removeUserGroupFromFolder(groupId: string, folderId: string): Observable<void> {
    return this.http.delete<void>(`${this.config.apiEndpoint}/groups/${groupId}/folders/${folderId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<GroupFolderMin[]>(`group_folders`, folderGroups => {
          if (!folderGroups) return;
          return folderGroups.filter(gg => gg.folder !== folderId || gg.group !== groupId);
        });
      })
    );
  }

  // This method is currently needed, but can be removed once the frontend doesn't need to check for access levels anymore
  private getAllGroupGalleries() {
    return this.getUserGroups().pipe(
      switchMap(groups => {
        if (!groups.length) return of([]);
        return combineLatest(groups.map(group => this.getGroupGalleries(group.id))).pipe(
          map(galleries => galleries.flat())
        );
      })
    );
  }

  // This method is currently needed, but can be removed once the frontend doesn't need to check for access levels anymore
  private getAllGroupFolders() {
    return this.getUserGroups().pipe(
      switchMap(groups => {
        if (!groups.length) return of([]);
        return combineLatest(groups.map(group => this.getGroupFolders(group.id))).pipe(
          map(galleries => galleries.flat())
        );
      })
    );
  }
}
